import { useState, useCallback, useEffect } from 'react';
import { GameBoard } from '../GameBoard';
import { useGameState, useBot } from '../../hooks';
import { GameAction } from '../../types/game';
import './BotGameScreen.css';

interface BotGameScreenProps {
  onBackToLobby: () => void;
}

export function BotGameScreen({ onBackToLobby }: BotGameScreenProps) {
  const [combiningCards, setCombiningCards] = useState<string[] | null>(null);

  const gameStateHook = useGameState();
  const { gameState, playerId } = gameStateHook;

  // Initialize game on mount
  useEffect(() => {
    gameStateHook.setPlayerId(1);
    gameStateHook.setIsHost(true);
    gameStateHook.initGame(Date.now(), 'bot-offline');
  }, []);

  const handleCombineAnimation = useCallback((cardIds: [string, string]) => {
    setCombiningCards(cardIds);
  }, []);

  const handleCombineAnimationEnd = useCallback(() => {
    setCombiningCards(null);
  }, []);

  // Bot hook
  useBot({
    enabled: true,
    gameState,
    executeAction: gameStateHook.executeAction,
    onCombineAnimation: handleCombineAnimation,
    onCombineAnimationEnd: handleCombineAnimationEnd,
  });

  // Game actions
  const handleCombine = useCallback((cardId1: string, cardId2: string) => {
    if (!gameState || !playerId) return;
    if (gameState.currentTurn !== playerId) return;

    const action: GameAction = { type: 'combine', cardId1, cardId2 };
    gameStateHook.executeAction(playerId, action);
  }, [gameState, playerId, gameStateHook]);

  const handleDiscard = useCallback((cardIds: string[]) => {
    if (!gameState || !playerId) return;
    if (gameState.currentTurn !== playerId) return;

    const action: GameAction = { type: 'discard', cardIds };
    gameStateHook.executeAction(playerId, action);
  }, [gameState, playerId, gameStateHook]);

  const handleEndTurn = useCallback(() => {
    if (!gameState || !playerId) return;
    if (gameState.currentTurn !== playerId) return;

    const action: GameAction = { type: 'end_turn' };
    gameStateHook.executeAction(playerId, action);
  }, [gameState, playerId, gameStateHook]);

  const handleSurrender = useCallback(() => {
    if (!gameState || !playerId) return;
    if (gameState.winner !== null) return;

    const action: GameAction = { type: 'surrender' };
    gameStateHook.executeAction(playerId, action);
  }, [gameState, playerId, gameStateHook]);

  // Show loading until game is initialized
  if (!gameState) {
    return (
      <div className="bot-loading-screen">
        <h2>Starting bot game...</h2>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <GameBoard
      gameState={gameState}
      playerId={1}
      isSpectator={false}
      onCombine={handleCombine}
      onDiscard={handleDiscard}
      onEndTurn={handleEndTurn}
      onSurrender={handleSurrender}
      onBackToMenu={onBackToLobby}
      combiningCards={combiningCards}
    />
  );
}
